<template>
  <div class="py-4 container-fluid">
    <div class="mt-4 row">
      <div class="col-12">
        <div class="card">
          <div class="card-header border-bottom d-flex justify-content-between align-items-center">
            <h5 class="mb-0">Phonebot Update-Manager</h5>
            <button class="btn btn-primary btn-sm" @click="openCreateDialog">
              <i class="fas fa-plus me-1"></i> Neue Version
            </button>
          </div>

          <div class="table-responsive p-3">
            <table class="table table-flush align-middle">
              <thead class="thead-light">
                <tr>
                  <th>#</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>URL</th>
                  <th>SHA256</th>
                  <th>Install at</th>
                  <th>Released at</th>
                  <th>Notes</th>
                  <th class="text-end">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="9" class="text-center text-muted py-4">Lade Versionen …</td>
                </tr>
                <tr v-else-if="!versions.length">
                  <td colspan="9" class="text-center text-muted py-4">
                    Noch keine Versionen registriert.
                  </td>
                </tr>
                <tr v-else v-for="(v, idx) in versions" :key="v.id">
                  <td>{{ idx + 1 }}</td>
                  <td><strong>{{ v.version }}</strong></td>
                  <td>
                    <span class="badge" :class="v.is_active ? 'badge-success' : 'badge-secondary'">
                      {{ v.is_active ? 'aktiv' : 'inaktiv' }}
                    </span>
                  </td>
                  <td class="text-truncate" style="max-width: 260px;" :title="v.url">
                    <a :href="v.url" target="_blank" rel="noopener">{{ v.url }}</a>
                  </td>
                  <td>
                    <code v-if="v.sha256" :title="v.sha256">{{ shortSha(v.sha256) }}</code>
                    <span v-else class="text-muted">—</span>
                  </td>
                  <td>{{ formatDate(v.install_at) }}</td>
                  <td>{{ formatDate(v.released_at) }}</td>
                  <td class="text-truncate" style="max-width: 240px;" :title="v.notes">
                    {{ v.notes || '—' }}
                  </td>
                  <td class="text-end">
                    <button
                      class="btn btn-sm btn-success me-1"
                      :disabled="v.is_active"
                      @click="activate(v)"
                      title="Diese Version aktivieren"
                    >
                      <i class="fas fa-check"></i>
                    </button>
                    <button
                      class="btn btn-sm btn-info me-1"
                      @click="openEditDialog(v)"
                      title="Bearbeiten"
                    >
                      <i class="fas fa-pen"></i>
                    </button>
                    <button
                      class="btn btn-sm btn-danger"
                      @click="remove(v)"
                      title="Löschen"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <div
      v-if="dialogOpen"
      class="modal-backdrop-custom"
      @click.self="closeDialog"
    >
      <div class="modal-card-custom">
        <div class="card">
          <div class="card-header border-bottom d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              {{ editing ? 'Version bearbeiten' : 'Neue Version anlegen' }}
            </h5>
            <button type="button" class="btn-close" @click="closeDialog"></button>
          </div>

          <form @submit.prevent="submit" class="card-body">
            <div class="mb-3">
              <label class="form-label">Version <span class="text-danger">*</span></label>
              <input
                v-model="form.version"
                type="text"
                class="form-control"
                placeholder="1.0.0.114"
                required
              />
              <small class="text-muted">Vier-stellige Versionsnummer, z.B. <code>1.0.0.114</code></small>
            </div>

            <div class="mb-3">
              <label class="form-label">Download-URL <span class="text-danger">*</span></label>
              <input
                v-model="form.url"
                type="url"
                class="form-control"
                placeholder="https://adam-ai.com/download/AI-Phone-Setup_v1-114.zip"
                required
              />
            </div>

            <div class="mb-3">
              <label class="form-label">SHA256 (optional)</label>
              <input
                v-model="form.sha256"
                type="text"
                class="form-control"
                maxlength="64"
                pattern="[A-Fa-f0-9]{64}"
                placeholder="64 Hex-Zeichen"
              />
              <small class="text-muted">Leer lassen, falls keine Prüfsumme verteilt wird.</small>
            </div>

            <div class="mb-3">
              <label class="form-label">Install at (UTC, optional)</label>
              <input
                v-model="form.install_at"
                type="datetime-local"
                class="form-control"
              />
              <small class="text-muted">
                Wann die Phonebots installieren sollen. Leer lassen für „sofort".
              </small>
            </div>

            <div class="mb-3">
              <label class="form-label">Notes (optional)</label>
              <textarea
                v-model="form.notes"
                class="form-control"
                rows="2"
                placeholder="z.B. Bugfix für SIP-Reconnect"
              ></textarea>
            </div>

            <div class="form-check mb-3">
              <input
                id="phonebotversion-active"
                v-model="form.is_active"
                type="checkbox"
                class="form-check-input"
              />
              <label class="form-check-label" for="phonebotversion-active">
                Direkt als aktive Version setzen
                <small class="text-muted d-block">Deaktiviert die bisher aktive Version automatisch.</small>
              </label>
            </div>

            <div class="d-flex justify-content-end gap-2">
              <button type="button" class="btn btn-outline-secondary" @click="closeDialog">
                Abbrechen
              </button>
              <button type="submit" class="btn btn-primary" :disabled="submitting">
                {{ submitting ? 'Speichern …' : (editing ? 'Speichern' : 'Anlegen') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from "sweetalert2";
import phonebotVersionService from "@/services/phonebot-version.service";

const emptyForm = () => ({
  version: "",
  url: "",
  sha256: "",
  notes: "",
  install_at: "",
  is_active: false,
});

export default {
  name: "PhonebotVersions",
  data() {
    return {
      versions: [],
      loading: false,
      dialogOpen: false,
      editing: null, // null = create, else version object being edited
      submitting: false,
      form: emptyForm(),
    };
  },

  async mounted() {
    await this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      try {
        this.versions = await phonebotVersionService.list();
      } catch (error) {
        console.error("Failed to load phonebot versions", error);
        Swal.fire("Fehler", "Versionen konnten nicht geladen werden.", "error");
      } finally {
        this.loading = false;
      }
    },

    openCreateDialog() {
      this.editing = null;
      this.form = emptyForm();
      this.dialogOpen = true;
    },

    openEditDialog(version) {
      this.editing = version;
      this.form = {
        version: version.version,
        url: version.url,
        sha256: version.sha256 || "",
        notes: version.notes || "",
        install_at: this.toLocalInput(version.install_at),
        is_active: !!version.is_active,
      };
      this.dialogOpen = true;
    },

    closeDialog() {
      if (this.submitting) return;
      this.dialogOpen = false;
      this.editing = null;
      this.form = emptyForm();
    },

    async submit() {
      this.submitting = true;
      try {
        const payload = {
          version: this.form.version.trim(),
          url: this.form.url.trim(),
          sha256: this.form.sha256.trim() || null,
          notes: this.form.notes.trim() || null,
          install_at: this.form.install_at ? this.fromLocalInput(this.form.install_at) : null,
          is_active: this.form.is_active,
        };

        if (this.editing) {
          await phonebotVersionService.update(this.editing.id, payload);
          Swal.fire("Gespeichert", `Version ${payload.version} wurde aktualisiert.`, "success");
        } else {
          await phonebotVersionService.create(payload);
          Swal.fire("Angelegt", `Version ${payload.version} wurde angelegt.`, "success");
        }
        this.dialogOpen = false;
        this.editing = null;
        this.form = emptyForm();
        await this.load();
      } catch (error) {
        console.error("Failed to save phonebot version", error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.errors?.version?.[0] ||
          "Version konnte nicht gespeichert werden.";
        Swal.fire("Fehler", message, "error");
      } finally {
        this.submitting = false;
      }
    },

    async activate(version) {
      const result = await Swal.fire({
        title: `Version ${version.version} aktivieren?`,
        text: "Die bisher aktive Version wird automatisch deaktiviert.",
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Aktivieren",
        cancelButtonText: "Abbrechen",
      });
      if (!result.isConfirmed) return;

      try {
        await phonebotVersionService.activate(version.id);
        Swal.fire("Aktiv", `Version ${version.version} ist jetzt aktiv.`, "success");
        await this.load();
      } catch (error) {
        console.error("Failed to activate version", error);
        Swal.fire("Fehler", "Version konnte nicht aktiviert werden.", "error");
      }
    },

    async remove(version) {
      const result = await Swal.fire({
        title: `Version ${version.version} löschen?`,
        text: "Dies kann nicht rückgängig gemacht werden.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Löschen",
        cancelButtonText: "Abbrechen",
        confirmButtonColor: "#d33",
      });
      if (!result.isConfirmed) return;

      try {
        await phonebotVersionService.remove(version.id);
        Swal.fire("Gelöscht", `Version ${version.version} wurde gelöscht.`, "success");
        await this.load();
      } catch (error) {
        console.error("Failed to delete version", error);
        Swal.fire("Fehler", "Version konnte nicht gelöscht werden.", "error");
      }
    },

    formatDate(value) {
      if (!value) return "—";
      // Accept "YYYY-MM-DD HH:MM:SS" or "YYYY-MM-DDTHH:MM:SS[.ms][Z|+HH:MM]";
      // return "YYYY-MM-DD HH:MM" — wall-clock as stored, no timezone math.
      const s = String(value).replace("T", " ");
      return s.length >= 16 ? s.slice(0, 16) : "—";
    },

    /** Pass the backend string through to <input type="datetime-local"> as-is (no timezone conversion). */
    toLocalInput(value) {
      if (!value) return "";
      const s = String(value).replace(" ", "T");
      return s.length >= 16 ? s.slice(0, 16) : "";
    },

    /** Send the datetime-local value back to the backend as a naive wall-clock string (no timezone). */
    fromLocalInput(value) {
      if (!value) return null;
      // datetime-local gives "YYYY-MM-DDTHH:MM"; backend wants "YYYY-MM-DD HH:MM:SS".
      return value.replace("T", " ") + ":00";
    },

    shortSha(sha) {
      if (!sha) return "";
      return sha.length > 12 ? sha.substring(0, 12) + "…" : sha;
    },
  },
};
</script>

<style scoped>
.badge-success {
  background-color: #28a745;
}
.badge-secondary {
  background-color: #6c757d;
}
.btn-close {
  background: transparent;
  border: 0;
  font-size: 1.25rem;
  line-height: 1;
}
.btn-close::before {
  content: "\00d7";
}

/* Simple modal so we don't depend on Bootstrap's JS modal plugin. */
.modal-backdrop-custom {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1050;
  padding: 4rem 1rem 1rem;
  overflow-y: auto;
}
.modal-card-custom {
  width: 100%;
  max-width: 640px;
}
</style>
